/**
 * AI 流式探针（P2-1）
 *
 * ── 背景 ──────────────────────────────────────────────────────────────────
 * AI 对话是核心链路，但流式语义（首字快慢、中途断流、没正常收尾、连续失败）
 * 此前**完全无观测** —— 用户抱怨"卡住了/没反应"时，我们无法判断是模型慢、
 * 网络断，还是前端没处理好。本模块补齐这四类信号。
 *
 * ── 设计原则（务必守住）──────────────────────────────────────────────────
 * 1. **纯前端闭环**：只依据 fetch / reader 的本地事件判断，不依赖后端任何新增
 *    字段或接口。后端不配合也能正常工作（只是拿不到服务端侧的原因）。
 * 2. **零业务影响**：全程 try/catch；不改请求、不注入请求头、不额外发请求；
 *    探针自身的任何异常都不得影响对话主流程。
 * 3. **用户主动停止不算失败**：与 chat.service.ts 保持一致 —— 通过
 *    `AbortError` / `signal.aborted` 判定的中止，只记为 'abort'，
 *    **不计入失败率**（否则用户点"停止"会污染指标）。
 * 4. **只在异常态上报**：正常完成的流只报一条 TTFT 性能指标，不刷事件。
 */

import { reportCustomEvent, reportCustomPerfMetric } from '@/config/apm';
import { dumpErrorDetail } from './snapshot';
import { normalizeModelName } from './context';

// ─── 配置项（集中管理）────────────────────────────────────────────────────
const CONFIG = {
  /** 断流看门狗：超过此毫秒数未收到任何新数据则判定断流 */
  stallTimeoutMs: 30_000,
  /** 连续失败阈值：达到后上报 ai_continuous_fail 并清零重新计数 */
  continuousFailThreshold: 3,
  /** 连续失败按会话维度统计，key 为 chatId（unknown 归为同一组） */

  // ── ai_ttft 上报策略（2026-09-09 定：选「采样」方案，控制上报量）────────
  // 背景：TTFT 是延迟指标，全量上报才能算准 P50/P90；但每次对话都报会产生
  // 约 1300~2700 条/天，量偏大。故采用「慢请求全报 + 正常请求采样」：
  //   - 慢请求（≥ ttftSlowThresholdMs）：**100% 全量上报**，它本身就是异常，不能漏。
  //   - 正常请求：按 ttftSampleRate 采样上报，保留分位数的统计代表性、同时压住总量。
  // 权衡说明：这样会让样本中「慢请求」占比被放大 → P90/P95 略偏高（偏保守，
  // 更利于发现问题）；但 P50 主要由正常样本决定，基本不受影响，仍可用于看整体水位。
  /** 正常（未达慢阈值）对话的 TTFT 采样率，0~1，1 表示全量 */
  ttftSampleRate: 0.1,
  /**
   * TTFT 慢请求阈值（毫秒）：达到或超过则不受采样限制，100% 上报。
   * 取值依据（2026-09-09 定）：主流大模型首字延迟优秀 <1s、正常 1~2s，
   * 超过约 3s 用户开始明显感知"卡"（UX 3 秒定律）。取 4000ms 作为「慢」的门槛，
   * 既能覆盖用户会抱怨的区间，又不至于把大量正常波动算进来。
   * ⚠️ 上线 1~2 天后建议用真实 P90 复核此值（P90 是天然的慢阈值候选）。
   */
  ttftSlowThresholdMs: 4000,
} as const;

/** 按概率命中采样（用于正常请求的降级采样，压住上报量） */
const hitSample = (rate: number): boolean => {
  try {
    if (!(rate > 0)) return false;
    if (rate >= 1) return true;
    return Math.random() < rate;
  } catch {
    return false;
  }
};

/** 流结束原因 */
export type StreamEndReason =
  | 'done' // 正常收到 [DONE] 并完成
  | 'error' // 异常失败（网络错误 / 解析错误 / HTTP 非 2xx）
  | 'abort' // 用户主动停止（不计失败）
  | 'unfinished'; // 流结束了但既没 [DONE] 也没显式完成标志

export interface BeginStreamOptions {
  chatId?: string;
  modelName?: string;
}

export interface AiStreamProbe {
  /** 收到任意一块数据时调用（喂看门狗） */
  onChunk(): void;
  /** 收到第一个有效 token 时调用（用于计算 TTFT） */
  onFirstToken(): void;
  /** 流结束时调用；幂等，重复调用只有第一次生效 */
  finish(reason: StreamEndReason, detail?: Record<string, unknown>): void;
}

/**
 * 连续失败计数（按 chatId）
 *
 * ⚠️ 防内存泄漏：chatId 会随用户新建会话无限累积，若只 set 不清理，
 * 长会话（用户开很多会话）会让本 Map 缓慢膨胀。故设置上限，
 * 超过时先清掉「已归零」的条目（成功/已上报的），仍超限则整体兜底清空。
 */
const failStreak = new Map<string, number>();
const FAIL_STREAK_MAX = 200;

const pruneFailStreak = (): void => {
  try {
    if (failStreak.size < FAIL_STREAK_MAX) return;
    // 先清理已归零的（这些会话当前没有连续失败，删掉无副作用）
    for (const [key, val] of failStreak) {
      if (val <= 0) failStreak.delete(key);
    }
    // 仍超限（极端：大量会话都处于连续失败中）→ 整体清空，宁可丢计数也不膨胀
    if (failStreak.size >= FAIL_STREAK_MAX) failStreak.clear();
  } catch {
    /* 清理失败也不影响主流程 */
  }
};

/**
 * 开启一次流式请求的观测。
 * 调用方在流结束时必须调用 finish()，否则看门狗会在超时后自动收尾。
 */
export function beginAiStream(options: BeginStreamOptions = {}): AiStreamProbe {
  const startedAt = Date.now();
  const chatKey = options.chatId ?? 'unknown';
  const modelName = normalizeModelName(options.modelName);

  let firstTokenAt: number | null = null;
  let finished = false;
  let stallReported = false;
  let watchdog: ReturnType<typeof setTimeout> | null = null;

  const clearWatchdog = (): void => {
    try {
      if (watchdog) {
        clearTimeout(watchdog);
        watchdog = null;
      }
    } catch {
      /* 忽略 */
    }
  };

  // 断流看门狗：只在未收到首字前 + 收到数据后持续重置；触发一次后不再重设，
  // 避免长静默流反复上报。
  const armWatchdog = (): void => {
    try {
      clearWatchdog();
      if (finished || stallReported) return;
      watchdog = setTimeout(() => {
        if (finished || stallReported) return;
        stallReported = true;
        reportCustomEvent(
          'ai_stream_abnormal',
          { waitedMs: Date.now() - (firstTokenAt ?? startedAt) },
          {
            modelName,
            stage: firstTokenAt === null ? 'before_first_token' : 'mid_stream',
          }
        );
        dumpErrorDetail('ai_stream_abnormal', {
          chatId: options.chatId,
          modelName,
          stage: firstTokenAt === null ? 'before_first_token' : 'mid_stream',
          waitedMs: Date.now() - (firstTokenAt ?? startedAt),
        });
      }, CONFIG.stallTimeoutMs);
    } catch {
      /* 忽略 */
    }
  };

  const reportTtft = (): void => {
    try {
      if (firstTokenAt === null) return; // 没收到首字，TTFT 无意义
      const ttft = firstTokenAt - startedAt;

      // 慢请求全量上报（本身就是异常，绝不因采样漏掉）；正常请求按采样率上报，压住总量
      const isSlow = ttft >= CONFIG.ttftSlowThresholdMs;
      if (!isSlow && !hitSample(CONFIG.ttftSampleRate)) return;

      // 走自定义性能指标（分位/趋势由平台算）；需先在控制台注册指标名 ai_ttft
      // extra.sampled 标注本次是「慢请求全量(0)」还是「正常采样(1)」，便于看板识别与回溯
      reportCustomPerfMetric('ai_ttft', ttft, {
        modelName,
        sampled: isSlow ? '0' : '1',
      });
    } catch {
      /* 忽略 */
    }
  };

  const bumpFailStreak = (): void => {
    try {
      pruneFailStreak(); // 写入前先控制规模，防止 chatId 无限累积
      const next = (failStreak.get(chatKey) ?? 0) + 1;
      failStreak.set(chatKey, next);
      if (next >= CONFIG.continuousFailThreshold) {
        reportCustomEvent(
          'ai_continuous_fail',
          { failCount: next },
          { modelName }
        );
        dumpErrorDetail('ai_continuous_fail', {
          chatId: options.chatId,
          modelName,
          failCount: next,
        });
        failStreak.delete(chatKey); // 上报后清除，避免同一段连续失败反复告警（也避免占 Map）
      }
    } catch {
      /* 忽略 */
    }
  };

  const resetFailStreak = (): void => {
    try {
      // 直接删除而非置 0：不占 Map 空间，天然避免膨胀（读取时 ?? 0 语义等价）
      failStreak.delete(chatKey);
    } catch {
      /* 忽略 */
    }
  };

  armWatchdog();

  return {
    onChunk(): void {
      try {
        if (finished) return;
        armWatchdog();
      } catch {
        /* 忽略 */
      }
    },

    onFirstToken(): void {
      try {
        if (finished || firstTokenAt !== null) return; // 只记第一次
        firstTokenAt = Date.now();
        armWatchdog();
      } catch {
        /* 忽略 */
      }
    },

    finish(reason: StreamEndReason, detail: Record<string, unknown> = {}): void {
      try {
        if (finished) return; // 幂等
        finished = true;
        clearWatchdog();

        // 正常完成：报 TTFT，重置连续失败
        if (reason === 'done') {
          reportTtft();
          resetFailStreak();
          return;
        }

        // 用户主动停止：不计失败，也不报 TTFT（未完成，TTFT 无意义）
        if (reason === 'abort') {
          return;
        }

        // 异常 / 未正常收尾：上报事件
        reportTtft(); // 即便失败，已收到的首字延迟仍有诊断价值
        reportCustomEvent(
          reason === 'unfinished' ? 'ai_stream_unfinished' : 'ai_stream_error',
          { durationMs: Date.now() - startedAt },
          {
            modelName,
            reason,
            hasFirstToken: firstTokenAt === null ? '0' : '1',
          }
        );
        dumpErrorDetail('ai_stream_abnormal', {
          chatId: options.chatId,
          modelName,
          reason,
          durationMs: Date.now() - startedAt,
          ...detail,
        });

        // 仅真实异常计入连续失败；unfinished 不算（流已结束，只是没明确完成标志）
        if (reason === 'error') {
          bumpFailStreak();
        }
      } catch {
        /* 埋点失败绝不影响业务流程 */
      }
    },
  };
}
